import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { sponsorSchema, SponsorCreateType } from "@/schemas/sponsor-schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const Sponsor = {
  Create: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: async (data: SponsorCreateType) => {
          const response = await axiosInstance.post("/api/sponsors", data);
          return response.data;
        },
        onSuccess: () => {
          toast.success(
            "Thank you for your sponsorship! We'll be in touch soon."
          );
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to submit sponsorship";
          toast.error(message);
        },
        ...options,
      }),
  },

  Get: {
    useQuery: (query: { limit?: number; offset?: number } = {}, options = {}) =>
      useQuery({
        queryKey: ["Sponsors", query],
        queryFn: async () => {
          const params = new URLSearchParams();
          if (query.limit) params.append("limit", query.limit.toString());
          if (query.offset) params.append("offset", query.offset.toString());

          const url = `/api/sponsors${
            params.toString() ? `?${params.toString()}` : ""
          }`;
          return (await axiosInstance.get(url)).data;
        },
        ...options,
      }),
  },

  UpdateStatus: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: async ({ id, status }: { id: string; status: string }) => {
          return (await axiosInstance.patch(`/api/sponsors/${id}`, { status }))
            .data;
        },
        onSuccess: () => {
          toast.success("Sponsor status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["Sponsors"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update sponsor status";
          toast.error(message);
        },
        ...options,
      }),
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: async (id: string) => {
          return (await axiosInstance.delete(`/api/sponsors/${id}`)).data;
        },
        onSuccess: () => {
          toast.success("Sponsor deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Sponsors"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete sponsor";
          toast.error(message);
        },
        ...options,
      }),
  },
};

export default Sponsor;
